import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useDebounce} from './useDebounce';
import {api, UserAccountDto} from '../global/api';
import {EventBusContext} from '../providers/EventBus';
interface Props {
  fullSize?: boolean;
  pageSize?: number;
  keyWord?: string;
  additionalFilter?: Partial<UserAccountDto & {isActive?: boolean}>;
}

interface CacheEntry {
  data: UserAccountDto[];
  page: number;
  hasNextPage: boolean;
}

export const useSearchAccounts = ({keyWord, fullSize, additionalFilter = {}, pageSize = 25}: Props) => {
  const [searchTerm, setSearchTerm] = useState(keyWord ?? '');
  const debouncedSearch = useDebounce(searchTerm.trim(), 500);
  const [data, setData] = useState<UserAccountDto[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const {bus} = useContext(EventBusContext);
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());
  const pendingRequestsRef = useRef<Map<string, Promise<CacheEntry>>>(new Map());

  const additionalFilterKey = useMemo(() => JSON.stringify(additionalFilter ?? {}), [additionalFilter]);
  const parsedAdditionalFilter = useMemo(
    () => JSON.parse(additionalFilterKey) as Partial<UserAccountDto>,
    [additionalFilterKey],
  );
  const cacheKey = useMemo(() => `${debouncedSearch}::${additionalFilterKey}`, [debouncedSearch, additionalFilterKey]);

  const defaultSpec = useMemo(
    () => ({
      fio: debouncedSearch,
      ...parsedAdditionalFilter,
    }),
    [debouncedSearch, parsedAdditionalFilter],
  );

  useEffect(() => {
    cacheRef.current.clear();
  }, [additionalFilterKey]);

  const fetchPage = useCallback(
    async (pageToLoad: number, append = false) => {
      const requestKey = `${cacheKey}:${pageToLoad}`;
      if (pendingRequestsRef.current.has(requestKey)) {
        return pendingRequestsRef.current.get(requestKey)!;
      }

      setSearchLoading(true);
      const requestPromise = (async () => {
        try {
          let size = pageSize;
          if (fullSize) {
            if (pageToLoad > 0) {
              return cacheRef.current.get(cacheKey) ?? {data: [], page: 0, hasNextPage: false};
            }

            try {
              const {data: sizeData} = await api.accounts.getAccounts({...defaultSpec, page: 0, size: 1});
              size = sizeData.totalElements || 0;
            } catch {
              size = pageSize;
            }
          }

          const res = await api.accounts.getAccounts({...defaultSpec, page: pageToLoad, size});
          const content = res.data.content ?? [];
          const previousData = append ? (cacheRef.current.get(cacheKey)?.data ?? []) : [];
          const combinedData = append ? [...previousData, ...content] : content;
          const last = fullSize ? true : (res.data.last ?? true);
          const pageInfo: CacheEntry = {
            data: combinedData,
            page: pageToLoad,
            hasNextPage: fullSize ? false : !last,
          };
          cacheRef.current.set(cacheKey, pageInfo);
          setData(combinedData);
          setHasNextPage(pageInfo.hasNextPage);
          return pageInfo;
        } catch (err) {
          bus?.emit('error', [err]);
          throw err;
        } finally {
          pendingRequestsRef.current.delete(requestKey);
          setSearchLoading(false);
        }
      })();

      pendingRequestsRef.current.set(requestKey, requestPromise);
      return requestPromise;
    },
    [bus, cacheKey, defaultSpec, fullSize, pageSize],
  );

  useEffect(() => {
    const cachedResult = cacheRef.current.get(cacheKey);
    if (cachedResult) {
      setData(cachedResult.data);
      setHasNextPage(cachedResult.hasNextPage);
      setSearchLoading(false);
      return;
    }

    fetchPage(0);
  }, [cacheKey, fetchPage]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const fetchNextPage = async () => {
    const cachedResult = cacheRef.current.get(cacheKey);
    if (!cachedResult?.hasNextPage || searchLoading) {
      return;
    }

    await fetchPage(cachedResult.page + 1, true);
  };

  const resetSearch = () => {
    cacheRef.current.clear();
    pendingRequestsRef.current.clear();
    setData([]);
    setHasNextPage(false);
    setSearchLoading(false);
    setSearchTerm('');
  };

  return {handleSearch, data, searchTerm, searchLoading, fetchNextPage, hasNextPage, resetSearch};
};
